// Typed domain model for the GamingCloud control-plane (admin) API.
//
// Sources of truth:
//   - specs/01-leaderboard-config.md  (the full board config surface)
//   - specs/03-api.md                 (REST endpoints + shapes)
//   - specs/07-roadmap.md phases 3/5/6 (accounts, projects, api keys, boards)
//
// These are the UI-facing types. The live backend's wire shapes are adapted to them
// in src/api/client.ts (the only place that branches mock vs. live). The reconciled,
// true contract is documented in specs/03-api.md and web/README.md "API contract".

// ----------------------------------------------------------------------------
// Leaderboard configuration (specs/01-leaderboard-config.md)
// ----------------------------------------------------------------------------

export type SortOrder = "desc" | "asc";
export type ScoreType = "i64" | "f64";
export type DisplayAs =
  | "numeric"
  | "seconds"
  | "milliseconds"
  | "time_hms"
  | "percentage"
  | "currency";

export type UpdateMethod = "keep_best" | "force" | "increment" | "aggregate";
export type Aggregation = "last" | "min" | "max" | "sum" | "count" | "mean";

export type Tiebreaker =
  | "earliest_submission"
  | "latest_submission"
  | "subscore"
  | "lexicographic_id";

export type ResetInterval = "daily" | "weekly" | "monthly" | "cron";

export interface ResetConfig {
  interval: ResetInterval;
  /** CRON expression, only when interval === "cron" */
  cron?: string;
  /** IANA tz, defaults to UTC */
  reset_tz?: string;
}

export type ArchiveKind = "keep_all" | "keep_last_n" | "ttl_days";

export interface ArchivePolicy {
  kind: ArchiveKind;
  /** when kind === "keep_last_n" */
  keep_last_n?: number;
  /** when kind === "ttl_days" */
  ttl_days?: number;
}

export type WriteAuth = "server_only" | "client_signed";
export type Visibility = "public" | "private";

/** Full create/config payload — every knob from 01-leaderboard-config.md. */
/** Flexible branding for a board's public page (Gap 1). Configurable per-project (default) and
 *  per-leaderboard (overrides the project field by field). All fields optional; empty = inherit.
 *  The color/font/radius fields map onto the page's `--lb-*` CSS variables. */
export interface Branding {
  logo_url?: string;
  /** override the page title (else the board's display name) */
  title?: string;
  accent?: string;
  bg?: string;
  card?: string;
  text?: string;
  dim?: string;
  row?: string;
  highlight?: string;
  border?: string;
  /** font-family value, e.g. "Inter, system-ui, sans-serif" */
  font?: string;
  /** card corner radius, e.g. "14px" */
  radius?: string;
  /** raw CSS escape hatch appended last */
  custom_css?: string;
}

/** Per-board anti-cheat / moderation config (Gap 3). All fields optional; empty = off. */
export interface AntiCheat {
  /** flag a score more than this many stddevs better than the top-K mean (0/undefined = off) */
  anomaly_stddevs?: number | null;
  /** how many top entries to sample for the stddev (default 50) */
  anomaly_top_k?: number | null;
  /** don't run the stddev check until the board has this many entries (default 20) */
  anomaly_min_samples?: number | null;
  /** flag a self-improvement faster than this many score-units/second (0/undefined = off) */
  max_delta_per_sec?: number | null;
  /** hard per-player minimum seconds between submits (0/undefined = none) */
  cooldown_secs?: number | null;
  /** max submissions a player may make to the active version; further submits are rejected (Gap 4 tournaments). 0/undefined = unlimited */
  max_attempts?: number | null;
  /** hold flagged submissions for review (kept out of the leaderboard until approved) */
  hold_for_review?: boolean | null;
}

/** A flagged (suspicious) score awaiting review (Gap 3). */
export interface FlaggedScore {
  id: number;
  project: string;
  leaderboard_id: string;
  version: number;
  player_id: string;
  score: number;
  reason: string;
  detail: { message?: string; detail?: unknown };
  status: "pending" | "approved" | "rejected";
  created_at: string;
  resolved_at?: string | null;
  resolved_by?: string | null;
}

export interface LeaderboardConfig {
  id: string;
  /** human-readable display name (defaults to the id for older boards) */
  name: string;
  sort_order: SortOrder;
  score_type: ScoreType;
  display_as: DisplayAs;
  update_method: UpdateMethod;
  /** required only when update_method === "aggregate" */
  aggregation?: Aggregation;
  tiebreaker: Tiebreaker[];
  /** "never" represented as null reset */
  reset: ResetConfig | null;
  archive_policy: ArchivePolicy;
  /** optional cap; keeps top-N (or bottom-N for asc) */
  max_entries?: number | null;
  unique_by: "player_id";
  write_auth: WriteAuth;
  /** per-player submissions per minute; null = unlimited */
  rate_limit?: number | null;
  visibility: Visibility;
  /** opaque per-board metadata blob */
  entry_metadata_schema?: Record<string, string> | null;
  /** Gap 1: per-leaderboard public-page branding (overrides the project default). */
  branding?: Branding;
  /** Gap 3: per-board anti-cheat / moderation config. */
  anticheat?: AntiCheat;
}

/** Phase 11: the time-window family a board belongs to. */
export interface WindowView {
  type: "tumbling" | "rolling";
  /** rolling only: trailing horizon in seconds */
  horizon_secs?: number;
  /** rolling only: bucket granularity in seconds (sum/count/mean) */
  granularity_secs?: number;
}

/** Board as returned by GET — config + live runtime metadata. */
export interface Leaderboard extends LeaderboardConfig {
  size: number;
  current_version: number;
  /** Phase 11: the board's time-window family. */
  window?: WindowView;
  /** Phase 11.4: present iff this board is a GROUP parent — its child window names. Each child
   *  is an ordinary board at `<id>:<window>`. */
  windows?: string[] | null;
  /** owner node id from the cluster ring (specs/02) — internal, not shown in UI */
  owner_node?: string | null;
  /** RFC3339 timestamp of the last score write; null if the board has never had a score */
  last_activity_at?: string | null;
  /** count of score updates in the last 24 h */
  recent_updates?: number;
  created_at: string;
  updated_at: string;
}

/** Create payload: full config, plus an optional `windows` list that turns it into a board
 *  group (Phase 11.4) — one child board is created per window. */
export type CreateBoardInput = LeaderboardConfig & { windows?: string[] };

export interface BoardVersion {
  version: number;
  archived_at: string | null; // null = current/live
  size: number;
}

// ----------------------------------------------------------------------------
// Entries / scores (specs/03-api.md)
// ----------------------------------------------------------------------------

export interface Entry {
  rank: number;
  player_id: string;
  score: number;
  subscore?: number[];
  metadata?: Record<string, unknown> | null;
}

export interface SubmitScoreResult {
  rank: number;
  score: number;
  previous_rank: number | null;
  changed: boolean;
}

// ----------------------------------------------------------------------------
// Player-centric view: players → the boards they've contributed to → submissions
// ----------------------------------------------------------------------------

/** A player and a summary of their activity across a whole project. */
export interface PlayerSummary {
  player_id: string;
  submission_count: number;
  board_count: number;
  /** RFC3339 of the player's most recent submission anywhere in the project. */
  last_activity: string;
}

/** One board a player has submitted to, with a per-board activity summary. */
export interface PlayerBoard {
  leaderboard_id: string;
  submission_count: number;
  valid_count: number;
  pending_count: number;
  rejected_count: number;
  /** score of the player's most recent submission on this board */
  latest_score: number;
  last_activity: string;
}

/** One submission in a player's ledger history (newest first in lists). */
export interface PlayerSubmission {
  id: number;
  score: number;
  subscores?: number[];
  metadata?: Record<string, unknown> | null;
  status: "valid" | "pending" | "rejected";
  created_at: string;
}

/** Short-lived, read-only operator subscribe token for live WebSocket ranks. */
export interface ViewToken {
  token: string;
  expires_at: number;
}

// ----------------------------------------------------------------------------
// Control plane: accounts, projects (roadmap Phase 3)
// ----------------------------------------------------------------------------

export interface User {
  id: string;
  email: string;
  name: string;
  email_verified: boolean;
  created_at: string;
}

export type ProjectRole = "owner" | "admin" | "member";

export interface Project {
  id: string;
  name: string;
  slug: string;
  role: ProjectRole;
  board_count: number;
  /** Gap 1: project-level default branding for public pages. */
  branding?: Branding;
  created_at: string;
}

export interface Member {
  user_id: string;
  email: string;
  name: string;
  role: ProjectRole;
}

// ----------------------------------------------------------------------------
// API keys (roadmap Phase 5)
// ----------------------------------------------------------------------------

export type KeyScope = "read" | "write" | "admin";

export interface ApiKey {
  id: string;
  name: string;
  /** non-secret display prefix, e.g. "gck_live_7f3a…" */
  prefix: string;
  scopes: KeyScope[];
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

/** Returned ONCE on creation — the only time the raw secret is visible. */
export interface ApiKeyCreated extends ApiKey {
  secret: string;
}

// ----------------------------------------------------------------------------
// Plans / billing (roadmap Phase 10)
// ----------------------------------------------------------------------------

export type PlanKey = "free" | "indie" | "pro";

/** Mirrors the backend `PlanView` (`leaderboard/src/admin/usage.rs`). */
export interface PlanView {
  key: PlanKey;
  name: string;
  max_projects: number | null;
  max_boards: number | null;
  monthly_writes: number | null;
  max_ws_connections: number | null;
  monthly_events_soft: number | null;
  monthly_price_cents: number;
  overage_per_million_writes_cents: number | null;
  /** Maximum entries per individual leaderboard. `null` = unlimited. */
  max_entries_per_board: number | null;
}

export interface UsageCounts {
  writes: number;
  events: number;
  /** Cumulative replay/ghost blob bytes stored this period (Gap 2). Metered, no hard quota. */
  blob_bytes: number;
  board_count: number;
  project_count: number;
}

/** Mirrors the backend `UsageView` (`leaderboard/src/admin/usage.rs`) — nested plan + usage. */
export interface UsageView {
  account_id: string;
  /** Billing period (first of the month, UTC) as YYYY-MM-DD. */
  period: string;
  plan: PlanView;
  usage: UsageCounts;
  over_write_quota: boolean;
  over_board_limit: boolean;
}

export interface AccountCreated {
  account_id: string;
  name: string;
  role: string;
  plan_key: PlanKey;
}

// ----------------------------------------------------------------------------
// Error envelope (specs/03-api.md)
// ----------------------------------------------------------------------------

export interface ApiErrorEnvelope {
  error: { code: string; message: string };
}

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}
